import type { RefObject } from 'react';

import { Button, OtpInput, type OtpInputHandle, Spinner, Typography } from '@/shared/design-system';

import { InlineError } from '../../styled';
import { TotpSetupPhase } from '../../totp-management-types';
import { OtpBlock, PendingStatus } from './totp-setup.styled';

export type TotpSetupVerificationProps = {
  code: string;
  error: string | null;
  phase: TotpSetupPhase;
  onCodeChange: (_code: string) => void;
  onRetryActivation: () => void;
  otpRef: RefObject<OtpInputHandle | null>;
  locked?: boolean;
};

export const TotpSetupVerification = ({
  code,
  error,
  phase,
  onCodeChange,
  onRetryActivation,
  otpRef,
  locked = false,
}: TotpSetupVerificationProps) => {
  const activating = phase === TotpSetupPhase.Activating;
  const activationFailed = phase === TotpSetupPhase.ActivationFailed;
  const otpDisabled = activating || activationFailed || locked;
  const verificationError = phase === TotpSetupPhase.Verifying ? error : null;

  return (
    <>
      <Typography variant="body2">
        Enter the six-digit code from your authenticator app to confirm setup.
      </Typography>
      <OtpBlock>
        <OtpInput
          ref={otpRef}
          length={6}
          groupSize={3}
          value={code}
          onChange={onCodeChange}
          disabled={otpDisabled}
          error={Boolean(verificationError)}
        />
        {verificationError && (
          <InlineError role="alert" variant="body3">
            {verificationError}
          </InlineError>
        )}
        {activating && (
          <PendingStatus
            role="status"
            aria-label="Activation in progress"
            aria-live="polite"
            aria-busy="true"
          >
            <Spinner />
            <Typography variant="body3" color="inherit">
              Enabling two-factor authentication…
            </Typography>
          </PendingStatus>
        )}
        {activationFailed && (
          <>
            <InlineError role="alert" variant="body3">
              {error ?? 'Two-factor authentication could not be enabled.'}
            </InlineError>
            <Button size="sm" variant="outline" onClick={onRetryActivation}>
              Retry
            </Button>
          </>
        )}
      </OtpBlock>
    </>
  );
};
